import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
}

interface Lead {
  name: string;
  company: string;
  email: string;
  gdprConsent: boolean;
  meetingPreference: 'phone' | 'video' | 'in-person' | 'any';
  bestContactTime: string;
  includeConversation: boolean;
  meetingNotes?: string;
}

interface ChatState {
  isOpen: boolean;
  messages: Message[];
  isLoading: boolean;
  sessionToken: string;
  showLeadForm: boolean;
  lead: Lead | null;
  error: string | null;
  isTyping: boolean;
}

type ChatAction =
  | { type: 'TOGGLE_CHAT' }
  | { type: 'OPEN_CHAT' }
  | { type: 'CLOSE_CHAT' }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_TYPING'; payload: boolean }
  | { type: 'SHOW_LEAD_FORM' }
  | { type: 'HIDE_LEAD_FORM' }
  | { type: 'SET_LEAD'; payload: Lead }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESTORE_SESSION'; payload: { messages: Message[]; sessionToken: string } };

const generateSessionToken = () => crypto.randomUUID();

const getStoredSession = () => {
  try {
    const stored = localStorage.getItem('brick-chat-session');
    if (stored) {
      const { messages, sessionToken, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
        return {
          messages: (messages as Array<Omit<Message, 'timestamp'> & { timestamp: string }>).map((msg) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          })),
          sessionToken,
        };
      }
    }
  } catch (error) {
    console.error('Error loading stored session:', error);
  }
  return null;
};

const storedSession = getStoredSession();

const initialState: ChatState = {
  isOpen: false,
  messages: storedSession?.messages || [],
  isLoading: false,
  sessionToken: storedSession?.sessionToken || generateSessionToken(),
  showLeadForm: false,
  lead: null,
  error: null,
  isTyping: false,
};

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'TOGGLE_CHAT':
      return { ...state, isOpen: !state.isOpen };
    case 'OPEN_CHAT':
      return { ...state, isOpen: true };
    case 'CLOSE_CHAT':
      return { ...state, isOpen: false, showLeadForm: false };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_TYPING':
      return { ...state, isTyping: action.payload };
    case 'SHOW_LEAD_FORM':
      return { ...state, showLeadForm: true };
    case 'HIDE_LEAD_FORM':
      return { ...state, showLeadForm: false };
    case 'SET_LEAD':
      return { ...state, lead: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'RESTORE_SESSION':
      return {
        ...state,
        messages: action.payload.messages,
        sessionToken: action.payload.sessionToken,
      };
    default:
      return state;
  }
}

interface ChatContextType {
  state: ChatState;
  dispatch: React.Dispatch<ChatAction>;
  sendMessage: (message: string) => Promise<void>;
  submitLead: (lead: Lead) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  useEffect(() => {
    if (state.messages.length > 0) {
      localStorage.setItem(
        'brick-chat-session',
        JSON.stringify({
          messages: state.messages,
          sessionToken: state.sessionToken,
          timestamp: Date.now(),
        })
      );
    }
  }, [state.messages, state.sessionToken]);

  const sendMessage = async (message: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content: message,
      role: 'user',
      timestamp: new Date(),
    };
    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
    dispatch({ type: 'SET_TYPING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const { data, error } = await supabase.functions.invoke('chat-response', {
        body: {
          sessionToken: state.sessionToken,
          message,
          ipAddress: 'unknown',
          userAgent: navigator.userAgent,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to get response from chatbot');
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: data.response,
        role: 'assistant',
        timestamp: new Date(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: botMessage });
    } catch (error: unknown) {
      console.error('Error sending message:', error);
      const message = error instanceof Error ? error.message : 'Something unexpected happened. Please try again.';
      dispatch({ type: 'SET_ERROR', payload: message });
    } finally {
      dispatch({ type: 'SET_TYPING', payload: false });
    }
  };

  const submitLead = async (lead: Lead) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const { error } = await supabase.functions.invoke('submit-lead', {
        body: {
          sessionToken: state.sessionToken,
          name: lead.name,
          company: lead.company,
          email: lead.email,
          gdprConsent: lead.gdprConsent,
          meetingPreference: lead.meetingPreference,
          bestContactTime: lead.bestContactTime,
          includeConversation: lead.includeConversation,
          meetingNotes: lead.meetingNotes,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to submit lead');
      }

      dispatch({ type: 'SET_LEAD', payload: lead });
      dispatch({ type: 'HIDE_LEAD_FORM' });

      const successMessage: Message = {
        id: Date.now().toString(),
        content:
          "Perfect! Your meeting request has been submitted. We'll reach out within 24 hours to schedule your meeting. Brick moves fast—expect real solutions, not consultancy fluff.",
        role: 'assistant',
        timestamp: new Date(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: successMessage });
    } catch (error: unknown) {
      console.error('Error submitting lead:', error);
      const message = error instanceof Error ? error.message : 'Something unexpected happened. Please try again.';
      dispatch({ type: 'SET_ERROR', payload: message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  return <ChatContext.Provider value={{ state, dispatch, sendMessage, submitLead }}>{children}</ChatContext.Provider>;
};
